/**
 * NewFilesModal Component
 * Displays a popup modal showing new files added since the user's last login
 */

import React, { useState } from 'react';

// File type to icon mapping (reused from FileCard)
const getFileIcon = (extension) => {
  const ext = (extension || '').toLowerCase();
  const iconMap = {
    // Documents
    pdf: { icon: 'fa-file-pdf', className: 'pdf' },
    doc: { icon: 'fa-file-word', className: 'doc' },
    docx: { icon: 'fa-file-word', className: 'docx' },
    xls: { icon: 'fa-file-excel', className: 'xls' },
    xlsx: { icon: 'fa-file-excel', className: 'xlsx' },
    ppt: { icon: 'fa-file-powerpoint', className: 'ppt' },
    pptx: { icon: 'fa-file-powerpoint', className: 'pptx' },
    txt: { icon: 'fa-file-alt', className: 'txt' },
    csv: { icon: 'fa-file-csv', className: 'csv' },
    // Images
    jpg: { icon: 'fa-file-image', className: 'image' },
    jpeg: { icon: 'fa-file-image', className: 'image' },
    png: { icon: 'fa-file-image', className: 'image' },
    gif: { icon: 'fa-file-image', className: 'image' },
    svg: { icon: 'fa-file-image', className: 'image' },
    webp: { icon: 'fa-file-image', className: 'image' },
    // Video
    mp4: { icon: 'fa-file-video', className: 'video' },
    mov: { icon: 'fa-file-video', className: 'video' },
    avi: { icon: 'fa-file-video', className: 'video' },
    webm: { icon: 'fa-file-video', className: 'video' },
    // Audio
    mp3: { icon: 'fa-file-audio', className: 'audio' },
    wav: { icon: 'fa-file-audio', className: 'audio' },
    // Archives
    zip: { icon: 'fa-file-archive', className: 'archive' },
    rar: { icon: 'fa-file-archive', className: 'archive' },
    '7z': { icon: 'fa-file-archive', className: 'archive' },
    // Code
    js: { icon: 'fa-file-code', className: 'code' },
    html: { icon: 'fa-file-code', className: 'code' },
    css: { icon: 'fa-file-code', className: 'code' },
    json: { icon: 'fa-file-code', className: 'code' },
  };

  return iconMap[ext] || { icon: 'fa-file', className: 'default' };
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const NewFilesModal = ({
  isOpen,
  onClose,
  files = [],
  totalCount = 0,
  onDownload,
  onNavigateToFile
}) => {
  const [downloadingIds, setDownloadingIds] = useState(new Set());

  if (!isOpen) return null;

  const handleDownload = async (e, file) => {
    e.stopPropagation();
    if (downloadingIds.has(file.id)) return;

    setDownloadingIds(prev => new Set([...prev, file.id]));
    try {
      await onDownload(file.id, file.name, file.extension);
    } finally {
      setDownloadingIds(prev => {
        const next = new Set(prev);
        next.delete(file.id);
        return next;
      });
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleFileClick = (file) => {
    if (onNavigateToFile) {
      onNavigateToFile(file.parent_folder_id || file.folder_id);
    }
    onClose();
  };

  return (
    <div className="rf-modal-overlay" onClick={handleOverlayClick}>
      <div className="rf-modal rf-new-files-modal">
        <div className="rf-modal-header">
          <div className="rf-modal-header-content">
            <div className="rf-modal-icon">
              <i className="fas fa-sparkles"></i>
            </div>
            <div className="rf-modal-title-group">
              <h2 className="rf-modal-title">New Files Available</h2>
              <p className="rf-modal-subtitle">
                {totalCount} new file{totalCount !== 1 ? 's' : ''} added since your last visit
              </p>
            </div>
          </div>
          <button className="rf-modal-close" onClick={onClose} aria-label="Close modal">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="rf-modal-body">
          {files.length === 0 ? (
            <div className="rf-modal-empty">
              <i className="fas fa-check-circle"></i>
              <p>You're all caught up! No new files since your last visit.</p>
            </div>
          ) : (
            <div className="rf-new-files-list">
              {files.map((file) => {
                const { icon, className } = getFileIcon(file.extension);
                const createdAt = file.created_at || file.createdAt || file.created || file.updated_at || file.updatedAt;
                const isDownloading = downloadingIds.has(file.id);

                return (
                  <div
                    key={file.id}
                    className="rf-new-file-item"
                    onClick={() => handleFileClick(file)}
                  >
                    <div className={`rf-new-file-icon ${className}`}>
                      <i className={`fas ${icon}`}></i>
                    </div>
                    <div className="rf-new-file-info">
                      <h4 className="rf-new-file-name" title={file.name}>
                        {file.name}
                      </h4>
                      <div className="rf-new-file-meta">
                        {file.extension && (
                          <span className="rf-new-file-ext">{file.extension.toUpperCase()}</span>
                        )}
                        {file.size && <span>{formatFileSize(file.size)}</span>}
                        {createdAt && <span>{formatDate(createdAt)}</span>}
                      </div>
                    </div>
                    <button
                      className="rf-new-file-download"
                      onClick={(e) => handleDownload(e, file)}
                      disabled={isDownloading}
                      title="Download file"
                    >
                      {isDownloading ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : (
                        <i className="fas fa-download"></i>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="rf-modal-footer">
          {files.length > 0 && totalCount > files.length && (
            <p className="rf-modal-more-files">
              Showing {files.length} of {totalCount} new files
            </p>
          )}
          <button className="rf-btn rf-btn-primary" onClick={onClose}>
            Got it, thanks!
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewFilesModal;
